import type { z } from 'zod';

import { AccountRefSchema } from '../../users/account-ref/account-ref';

import { DiscountGrantSchema } from '../discount-grant/discount-grant';

/**
 * A grants-viewer row: the grant, plus the member who holds it.
 *
 * `DiscountGrant` carries `userId` and nothing else, because that is the shape
 * the repository and the member's own surface speak. A support queue cannot be
 * read that way — a uuid is not a person, and the whole question this viewer
 * answers is "why has *this member* not got their discount?".
 *
 * **It is an extension rather than a field on the base, and that is load-bearing
 * rather than tidy.** `MemberDiscountGrantSchema` is derived from
 * {@link DiscountGrantSchema} by `.omit()`, so a `member` added to the base
 * would flow straight into the *member's own* grant list — a staff account
 * lookup arriving on a mobile surface that must never carry one.
 *
 * **Why the lookup is server-side.** `GET /admin/users` answers to
 * `members.read`, and the operator this screen exists for holds `courses.read`
 * — a different key (see the catalogue's own note beside `courses.read`). A
 * client-side resolution would therefore name the member for an `admin` and draw
 * a uuid for the support role the queue was built for. So the lookup crosses the
 * boundary on the QueryBus (N6), batched once per page rather than once per row
 * (P14b), and this contract is what comes back out of it.
 *
 * **`null` means the lookup came back short, and nothing else.**
 * `discount_grants.user_id` is `notNull`, so unlike Trust — where a blocked
 * registration genuinely has no subject — there is no domain reading of an
 * absent member here: it is a hard-deleted account, or a users module answering
 * for fewer ids than were asked. The grant is still a row an operator may need
 * to revoke, so the viewer draws "no longer available" rather than letting a
 * mapper throw and take the whole page with it. Same argument, same shape, as
 * `ReportDetailSchema`.
 */
export const DiscountGrantDetailSchema = DiscountGrantSchema.extend({
  member: AccountRefSchema.nullable(),
}).strict();

export type DiscountGrantDetail = z.infer<typeof DiscountGrantDetailSchema>;
